import { downloadFile } from '@lib/files/download';
import { action, computed, makeObservable, observable } from 'mobx';
import PQueue from 'p-queue';
import { userStore } from '../User';

const queue = new PQueue({ concurrency: 16, interval: 1000, intervalCap: 2 });

export type RenderingClip = {
  mediaId: string;
  libraryId: string;
  startTime: number;
  endTime: number;
};

export class Renderer {
  @observable
  private renderingQueue: Set<string> = new Set();

  constructor() {
    makeObservable(this);
  }

  @computed
  get isRendering() {
    return this.renderingQueue.size > 0;
  }

  @action
  addToRenderingQueue(uuid: string) {
    this.renderingQueue.add(uuid);
  }

  @action
  removeFromRenderingQueue(uuid: string) {
    this.renderingQueue.delete(uuid);
  }

  private async renderV2<T extends object>(endpoint: string, body: T): Promise<string> {
    const { url } = await userStore.apiWithAuth<{ url: string }>(endpoint, {
      method: 'POST',
      body,
    });
    return url;
  }

  @observable
  isDownloading = false;

  @action
  setIsDownloading = (value: boolean) => {
    this.isDownloading = value;
  };

  createCompilation = async (
    clips: RenderingClip[],
    filename: string,
    height: number = 720,
    watermark: boolean = true,
  ) => {
    const uuid = crypto.randomUUID();
    this.addToRenderingQueue(uuid);

    try {
      const fileUrl = await queue.add(async () => {
        return this.renderV2('/rendering/compilation/v2', {
          clips: clips.map((clip) => {
            return {
              libraryId: clip.libraryId,
              mediaId: clip.mediaId,
              start: clip.startTime,
              end: clip.endTime,
            };
          }),
          height,
          filename,
          watermark: watermark ? 'starzero' : false,
        });
      });

      if (!fileUrl) {
        throw new Error('Failed to render compilation');
      }

      this.setIsDownloading(true);
      await downloadFile(fileUrl);
      this.setIsDownloading(false);
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      this.removeFromRenderingQueue(uuid);
    }
  };

  createGif = async (
    libraryId: string,
    mediaId: string,
    startTime: number,
    endTime: number,
    filename: string,
  ) => {
    const uuid = crypto.randomUUID();
    this.addToRenderingQueue(uuid);

    try {
      const fileUrl = await queue.add(async () => {
        return this.renderV2(`/library/${libraryId}/media/${mediaId}/gif/v2`, {
          start: startTime,
          end: endTime,
          filename,
        });
      });

      if (!fileUrl) {
        throw new Error('Failed to render gif');
      }

      await downloadFile(fileUrl);
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      this.removeFromRenderingQueue(uuid);
    }
  };
}
